import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { myLanes } from '../lib/support'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { messageOf } from '../lib/errors'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { Dialog } from '../components/Dialog'
import { FilterChips, type FilterChip } from '../components/FilterChips'
import { Picker } from '../components/Picker'
import { WarningsFlag } from '../components/admin/WarningsFlag'
import { SearchIcon } from '../components/icons'
import { EarningTable, EconomyStrip, RuleEditor, earningNotices } from '../components/StarEarningAdmin'
import { EndorsementEditor, EndorsementTable, endorsementNotices } from '../components/EndorsementAdmin'
import {
  activityLabel,
  loadEarningRules,
  loadStarEconomy,
  matchesEarningFilters,
  saveEarningRule,
  sourceLabel,
  type EarningRule,
  type StarEconomy,
} from '../lib/starEarning'
import {
  adminLabel,
  loadEndorsementAdmin,
  matchesEndorsementFilters,
  mergeEndorsement,
  saveEndorsement,
  unmergeEndorsement,
  type EndorsementAdminRow,
  type EndorsementFlag,
} from '../lib/endorsementAdmin'
import { loadMyStars } from '../lib/stars'

/**
 * Star One admin — #/admin/stars (what activities earn) and
 * #/admin/stars/endorsements (the endorsement words). `stars.admin`.
 *
 * ── MOVED UNDER ADMINISTRATION (David, 9 Oct) ───────────────────────────────
 *
 * It was a third tab on Star One (#/stars/admin, REC-47 / 099, 23 Sep), on the
 * argument that it is "about the star economy". The tab sat beside the Wall
 * and the Store, which are what ~900 people open Star One for, and setting
 * rates is not that. It is administration, so it is in the Administration
 * section with its own gate, beside Shop and stock. #/stars/admin still works:
 * App.tsx sends it here, so an old link lands on the right screen.
 *
 * ── PEOPLE AND ROLES' TREATMENT ─────────────────────────────────────────────
 *
 * The same page shape as routes/Admin.tsx (137): two thin rows above the
 * table — the section tabs with the count and the notices flag; then search
 * and pill filters, their choices as ×-able chips — the list on one surface,
 * and one row at a time in a right-hand tray. What used to stand above the
 * lists full time (two lead paragraphs, the ceiling, the month's four large
 * figures, the failed-award alert) is in the flag, the tray, or one line
 * under the table.
 *
 * Filtering is client-side: both reads are the whole set by design (the
 * catalogue is seven rows; endorsement_admin_list has no limit — rule 1's
 * stated exception). The rows keep the server's order. Nothing here sorts a
 * word or an activity by what it paid or how often it was used (D10, B22).
 *
 * ── GUARDED TWICE ───────────────────────────────────────────────────────────
 *
 * The nav item renders only for stars.admin, and every read and write behind
 * this page REFUSES without it (rule 12). The page's check is a courtesy.
 */
export type StarsAdminTab = 'earning' | 'endorsements'

type Group = 'Source' | 'Pays' | 'State' | 'Kind' | 'Shows as' | 'Spanish'

const KIND_LABEL = { once_ever: 'Once', monthly: 'Monthly' } as const
const STATE_LABEL = { on: 'On', off: 'Off' } as const
const OFFICIAL_LABEL = { official: 'Official', community: 'Community' } as const
const FLAG_LABEL: Record<EndorsementFlag, string> = {
  featured: 'Featured chip',
  hidden: 'Hidden from suggestions',
  merged: 'Has merged words',
}
const SPANISH_LABEL = { has: 'Has Spanish', missing: 'No Spanish yet' } as const

export function AdminStars({ me, tab = 'earning' }: { me: Me; tab?: StarsAdminTab }) {
  const canStarsAdmin = can(me, 'stars.admin')

  const [rules, setRules] = useState<EarningRule[] | null>(null)
  const [economy, setEconomy] = useState<StarEconomy | null>(null)
  const [allowance, setAllowance] = useState<number | null>(null)
  const [vocab, setVocab] = useState<EndorsementAdminRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [sources, setSources] = useState<string[]>([])
  const [kinds, setKinds] = useState<string[]>([])
  const [states, setStates] = useState<string[]>([])
  const [official, setOfficial] = useState<string[]>([])
  const [flags, setFlags] = useState<string[]>([])
  const [spanish, setSpanish] = useState<string[]>([])
  const [selected, setSelected] = useState<string | null>(null)

  // A new tab is a new list: the search, the filters and the tray belong to
  // the list they were made on.
  useEffect(() => {
    setSearch('')
    setSelected(null)
  }, [tab])


  // Both tabs' reads, once. Each is small, and the count beside the tabs and
  // the flag want both. Guarded on the permission: the server would refuse
  // anyway, and a refusal rendered as a red line to somebody who followed an
  // old link is noise.
  useEffect(() => {
    if (!canStarsAdmin) return
    let live = true
    Promise.all([loadEarningRules(), loadStarEconomy(), loadEndorsementAdmin()])
      .then(([r, e, v]) => {
        if (!live) return
        setRules(r)
        setEconomy(e)
        setVocab(v)
      })
      .catch((e: Error) => live && setError(messageOf(e)))
    // The giving allowance, for the ceiling notice only. Its own catch: the
    // notice says less without it rather than the page failing.
    loadMyStars()
      .then((s) => live && setAllowance(s.allowanceTotal))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [canStarsAdmin])

  // Save, then re-read from the database rather than splicing the row: the
  // screen shows what the table holds (028c), and "last changed by" is the
  // server's to say.
  async function saveRule(rule: { ruleKey: string; stars: number; monthlyCap: number | null; isEnabled: boolean }) {
    await saveEarningRule(rule)
    const [r, e] = await Promise.all([loadEarningRules(), loadStarEconomy()])
    setRules(r)
    setEconomy(e)
  }

  // Endorsement curation (128): write, then re-read the whole vocabulary — a
  // merge changes labels across the wall and the screen shows what the
  // database holds.
  async function afterVocab(write: () => Promise<void>) {
    await write()
    setVocab(await loadEndorsementAdmin())
  }

  const shownRules = useMemo(
    () =>
      (rules ?? []).filter((r) =>
        matchesEarningFilters(r, {
          query: search,
          sources,
          kinds: kinds as EarningRule['periodKind'][],
          states: states as ('on' | 'off')[],
        }),
      ),
    [rules, search, sources, kinds, states],
  )
  const shownVocab = useMemo(
    () =>
      (vocab ?? []).filter((r) =>
        matchesEndorsementFilters(r, {
          query: search,
          kinds: official as ('official' | 'community')[],
          flags: flags as EndorsementFlag[],
          spanish: spanish as ('has' | 'missing')[],
        }),
      ),
    [vocab, search, official, flags, spanish],
  )

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Star One admin"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={canStarsAdmin}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  if (!canStarsAdmin) {
    return shell(
      <Card title="Star One admin">
        <p className="empty">
          Setting what activities earn and curating endorsements needs the Star One admin role.
        </p>
      </Card>,
    )
  }

  // ── the filters for the tab you are on ─────────────────────────────────
  const earning = tab === 'earning'
  const sourceOptions = [...new Set((rules ?? []).map((r) => r.source))].map((s) => ({ value: s, label: sourceLabel(s) }))

  const chips: FilterChip[] = earning
    ? [
        ...sources.map((v) => ({ group: 'Source', value: v, label: sourceLabel(v) })),
        ...kinds.map((v) => ({ group: 'Pays', value: v, label: KIND_LABEL[v as keyof typeof KIND_LABEL] ?? v })),
        ...states.map((v) => ({ group: 'State', value: v, label: STATE_LABEL[v as keyof typeof STATE_LABEL] ?? v })),
      ]
    : [
        ...official.map((v) => ({ group: 'Kind', value: v, label: OFFICIAL_LABEL[v as keyof typeof OFFICIAL_LABEL] ?? v })),
        ...flags.map((v) => ({ group: 'Shows as', value: v, label: FLAG_LABEL[v as EndorsementFlag] ?? v })),
        ...spanish.map((v) => ({ group: 'Spanish', value: v, label: SPANISH_LABEL[v as keyof typeof SPANISH_LABEL] ?? v })),
      ]

  const setters: Record<Group, (fn: (v: string[]) => string[]) => void> = {
    Source: setSources,
    Pays: setKinds,
    State: setStates,
    Kind: setOfficial,
    'Shows as': setFlags,
    Spanish: setSpanish,
  }

  function clearFilters() {
    if (earning) {
      setSources([])
      setKinds([])
      setStates([])
    } else {
      setOfficial([])
      setFlags([])
      setSpanish([])
    }
    setSearch('')
  }

  const filtered = chips.length > 0 || search.trim() !== ''
  const warnings = earning
    ? rules ? earningNotices(rules, economy, allowance) : []
    : vocab ? endorsementNotices(vocab) : []
  // A failed award is the one thing on this page that needs acting on, so it
  // reaches the endorsements tab's flag too rather than waiting to be found.
  if (!earning && rules) warnings.unshift(...earningNotices(rules, economy, allowance).filter((w) => w.tone === 'warn'))

  const total = earning ? rules?.length ?? null : vocab?.length ?? null
  const shown = earning ? shownRules.length : shownVocab.length
  const noun = earning ? (total === 1 ? 'activity' : 'activities') : total === 1 ? 'word' : 'words'
  const officials = (vocab ?? []).filter((r) => r.isOfficial).length

  const openRule = earning ? rules?.find((r) => r.ruleKey === selected) ?? null : null
  const openWord = !earning ? vocab?.find((r) => r.id === selected) ?? null : null

  return shell(
    <div className="people starsadmin">
      {/* ── ROW 1: the two lists, the count, the notices ─────────────────── */}
      <div className="people__bar">
        <nav className="tabs starsadmin__tabs" aria-label="Star One admin sections">
          <Link
            to="/admin/stars"
            className={`tab${earning ? ' tab--on' : ''}`}
            aria-current={earning ? 'page' : undefined}
          >
            What activities earn
          </Link>
          <Link
            to="/admin/stars/endorsements"
            className={`tab${!earning ? ' tab--on' : ''}`}
            aria-current={!earning ? 'page' : undefined}
          >
            Endorsements
          </Link>
        </nav>
        <div className="people__actions">
          <p className="people__count" aria-live="polite">
            {total === null ? (
              'Loading…'
            ) : (
              <>
                {filtered && <><strong>{shown}</strong> of </>}
                <strong>{total}</strong> {noun}
                {!earning && <span className="people__countnote"> · {officials} official</span>}
              </>
            )}
          </p>
          <WarningsFlag warnings={warnings} />
        </div>
      </div>

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {/* ── ROW 2: search and the filters ────────────────────────────────── */}
      <div className="people__filters" role="search" aria-label={earning ? 'Find an activity' : 'Find an endorsement'}>
        <label className="people__search">
          <span className="searchwrap__icon" aria-hidden="true">
            <SearchIcon size={15} />
          </span>
          <span className="visually-hidden">{earning ? 'Find an activity' : 'Find a word'}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={earning ? 'Find an activity' : 'Find a word, an alias or its Spanish'}
            autoComplete="off"
          />
        </label>

        {earning ? (
          <>
            <Picker variant="pill" label="Source" searchPlaceholder="Source" options={sourceOptions} selected={sources} onChange={setSources} />
            <Picker
              variant="pill"
              label="Pays"
              searchPlaceholder="How often"
              options={[{ value: 'once_ever', label: 'Once' }, { value: 'monthly', label: 'Monthly' }]}
              selected={kinds}
              onChange={setKinds}
            />
            <Picker
              variant="pill"
              label="State"
              searchPlaceholder="On or off"
              options={[{ value: 'on', label: 'On' }, { value: 'off', label: 'Off' }]}
              selected={states}
              onChange={setStates}
            />
          </>
        ) : (
          <>
            <Picker
              variant="pill"
              label="Kind"
              searchPlaceholder="Official or community"
              options={[{ value: 'official', label: 'Official' }, { value: 'community', label: 'Community' }]}
              selected={official}
              onChange={setOfficial}
            />
            <Picker
              variant="pill"
              label="Shows as"
              searchPlaceholder="Featured, hidden, merged"
              options={(Object.keys(FLAG_LABEL) as EndorsementFlag[]).map((k) => ({ value: k, label: FLAG_LABEL[k] }))}
              selected={flags}
              onChange={setFlags}
            />
            <Picker
              variant="pill"
              label="Spanish"
              searchPlaceholder="Spanish label"
              options={[{ value: 'has', label: 'Has Spanish' }, { value: 'missing', label: 'No Spanish yet' }]}
              selected={spanish}
              onChange={setSpanish}
            />
          </>
        )}
      </div>

      <FilterChips
        chips={chips}
        onRemove={(c) => setters[c.group as Group]((v) => v.filter((x) => x !== c.value))}
        onClear={clearFilters}
      />

      <div className="people__tablecard">
        {total === null && !error && (
          <p className="empty people__empty" aria-busy="true">
            {earning ? 'Loading the earning rules…' : 'Loading the endorsements…'}
          </p>
        )}

        {total !== null && shown === 0 && (
          <div className="people__empty">
            <p className="empty">
              {filtered ? 'Nothing matches those filters.' : earning ? 'No activities.' : 'No endorsements yet.'}
            </p>
            {filtered && (
              <button type="button" className="btn btn--quiet btn--sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        )}

        {earning && shownRules.length > 0 && (
          <EarningTable rules={shownRules} selectedKey={selected} onSelect={setSelected} />
        )}
        {!earning && shownVocab.length > 0 && (
          <EndorsementTable rows={shownVocab} selectedId={selected} onSelect={setSelected} />
        )}
      </div>

      {earning && economy && <EconomyStrip economy={economy} />}

      {openRule && (
        <Dialog
          open
          title={activityLabel(openRule.ruleKey)}
          variant="tray"
          className="people__tray"
          onClose={() => setSelected(null)}
        >
          <RuleEditor key={openRule.ruleKey} rule={openRule} onSave={saveRule} />
        </Dialog>
      )}

      {openWord && (
        <Dialog
          open
          title={adminLabel(openWord)}
          variant="tray"
          className="people__tray"
          onClose={() => setSelected(null)}
        >
          <EndorsementEditor
            key={openWord.id}
            row={openWord}
            others={(vocab ?? []).filter((o) => o.id !== openWord.id)}
            onSave={(input) => afterVocab(() => saveEndorsement(input))}
            onMerge={async (from, into) => {
              await afterVocab(() => mergeEndorsement(from, into))
              // The merged word is an alias now, not a row: open the word it
              // went into, where Unmerge is.
              setSelected(into)
            }}
            onUnmerge={(id) => afterVocab(() => unmergeEndorsement(id))}
          />
        </Dialog>
      )}
    </div>,
  )
}

/** Same rule as the hub's avatar: first letters of the first two words. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}
